import Button from 'assets/components/button/Button';
import DataMap from 'assets/components/dataMap/DataMap';
import RenderProps from 'assets/components/helpers/RenderProps';
import Icon from 'assets/components/icon/Icon';
import { PageClasses, PageStructure } from 'assets/components/page/Page';
import Modal from 'assets/components/page/templates/Modal';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperBreadcrumbs from 'assets/themes/wrappers/Theme.Wrapper.Breadcrumbs';
import { resetScroll } from 'assets/utils/dom/Scroll';
import React, { useContext, useEffect, useState } from 'react';
import useForm from '../hooks/Form';

let ModalFormContext: React.Context<any>;

export function useModalForm<T>(): Template.ModalForm.Export<T> {
  return useContext(ModalFormContext);
}
export default function ModalForm<T>(props: Template.ModalForm.Import<T>) {
  const { lang } = getLocales();

  const currentTab = props.tabs?.length ? props.tabs[0] : undefined;
  const [activeTab, setActiveTab] = useState<Template.ModalForm.Tab<T> | undefined>(currentTab);

  const form = useForm<T>(
    {
      default: props.default,
      onSubmit: props.onSubmit,
      beforeUpdate: !props.beforeUpdate ? undefined : (data, updates) => props.beforeUpdate(data, updates, activeTab),
      validation: props.validation,
    },
    props.dependencyArray
  );
  const exportData: Template.ModalForm.Export<T> = { form, activeTab };
  const disabled = typeof props.disabled === 'function' ? props.disabled(exportData) : props.disabled;
  if (!ModalFormContext) ModalFormContext = React.createContext<Template.ModalForm.Export<T>>(exportData);

  const isLastTab =
    props.isQuery && props.tabs && activeTab
      ? props.tabs.findIndex((tab) => tab.id === activeTab.id) === props.tabs.length - 1
      : true;
  const isFirstTab =
    props.isQuery && props.tabs && activeTab ? props.tabs.findIndex((tab) => tab.id === activeTab.id) === 0 : true;
  const hasActiveTabErrors =
    typeof activeTab?.errors === 'function' ? !!activeTab.errors(exportData) : !!activeTab?.errors;

  const handleBackOrCancel = () => {
    if (!props.tabs || isFirstTab) {
      props.onCancel?.();
    } else {
      const currentIndex = props.tabs.findIndex((tab) => tab.id === activeTab?.id);
      setActiveTab(props.tabs[currentIndex - 1]);
    }
  };

  const handleNextOrSubmit = () => {
    if (isLastTab) {
      form.submit();
    } else {
      const currentIndex = props.tabs.findIndex((tab) => tab.id === activeTab?.id);
      setActiveTab(props.tabs[currentIndex + 1]);
    }
  };
  useEffect(() => {
    if (activeTab) {
      const container = document.querySelector<HTMLElement>(
        `.${PageStructure.page}.${PageClasses.ThemePageModal} .${PageStructure.pageContent}`
      );
      if (container) resetScroll(container);
    }
  }, [activeTab]);

  const isDisabled = disabled || (props.isQuery ? (isLastTab ? form.hasErrors : hasActiveTabErrors) : form.hasErrors);

  return (
    <ModalFormContext.Provider value={exportData}>
      <Modal
        subcomponents={props.subcomponents?.modal}
        onClose={props.onCancel}
        neverClose={props.neverClose}
        header={props.title}
        sidebar={props.sidebar}
        subHeader={
          props.tabs &&
          (props.isQuery ? (
            <div style={{ display: 'flex', width: '100%', backgroundColor: 'Window', color: 'ButtonText' }}>
              <div className={ThemeWrapperBreadcrumbs}>
                <DataMap
                  data={props.tabs}
                  render={({ data: tab }) => (
                    <div key={tab.id} id={tab.id} data-active={tab.id === activeTab?.id ? true : undefined}>
                      {tab.text}
                    </div>
                  )}
                />
              </div>
            </div>
          ) : (
            <DataMap
              data={props.tabs}
              render={({ data: tab }) => {
                const errors = typeof tab.errors === 'function' ? tab.errors(exportData) : tab.errors;
                return (
                  <Button
                    {...props.subcomponents?.tab}
                    key={tab.id}
                    data={tab}
                    onClick={() => setActiveTab(tab)}
                    active={activeTab?.id === tab.id}
                    style={
                      errors
                        ? {
                            color: Theme.Color.error,
                            fontWeight: 'bold',
                          }
                        : null
                    }
                    htmlElementProps={{
                      title: errors,
                    }}
                    media={errors ? <Icon class="fas-exclamation-triangle" /> : tab.icon && <Icon class={tab.icon} />}
                    disabled={typeof tab.disabled === 'function' ? tab.disabled(exportData) : tab.disabled}
                    render={tab.text}
                  />
                );
              }}
            />
          ))
        }
        render={RenderProps(props, 'render', exportData)}
        footer={
          RenderProps(props, 'footer', exportData) || (
            <>
              {RenderProps(props, 'footerButtons', exportData)}
              {props.isQuery ? (
                <Button
                  {...(props.subcomponents && props.subcomponents.cancelButton)}
                  onClick={handleBackOrCancel}
                  render={isFirstTab ? lang.cancel : lang.prev}
                  htmlElementProps={{ title: isFirstTab ? lang.cancel : lang.prev }}
                />
              ) : (
                props.onCancel &&
                !props.neverClose && (
                  <Button
                    {...(props.subcomponents && props.subcomponents.cancelButton)}
                    onClick={props.onCancel}
                    render={lang.cancel}
                    htmlElementProps={{ title: lang.cancel }}
                  />
                )
              )}
              &nbsp;
              <Button
                {...(props.subcomponents && props.subcomponents.submitButton)}
                onClick={handleNextOrSubmit}
                render={isLastTab ? props.submitButtonText || lang.submit : lang.next}
                disabled={isDisabled}
                active={!isDisabled}
                isLoading={isLastTab ? form.isSubmitted : false}
              />
              {RenderProps(props, 'footerButtonsRight', exportData)}
            </>
          )
        }
      />
    </ModalFormContext.Provider>
  );
}
